import { CloseOutlined } from "@ant-design/icons"
import { Flex } from "antd"
import React from "react"
import { useTranslation } from "react-i18next"

import { useAppDispatch } from "../../../hooks/use-app-dispatch.ts"
import { useAppSelector } from "../../../hooks/use-app-selector.ts"
import {
  clearFilters,
  selectActiveFilters,
  selectFacets,
  setFilters
} from "../redux/global-map.slice"
import { activeFilterChips } from "./active-filter-summary.ts"

/**
 * One chip per currently-set `GlobalMapFilters` field, each with its own "X"
 * that clears just that field, plus a "Clear All" once anything is set.
 * Self-contained the same way `GlobalMapFilterBar` is — reads
 * `selectActiveFilters`/`selectFacets` and dispatches `setFilters`/
 * `clearFilters` directly rather than taking them as props.
 *
 * Label resolution (`activeFilterChips`) degrades gracefully when
 * `selectFacets` is `null` — `fetchFacets` isn't dispatched anywhere yet
 * (Task 16) — by falling back to the raw stored id/value instead of
 * crashing or rendering "undefined".
 */
export function FilterChips() {
  const { t } = useTranslation()
  const dispatch = useAppDispatch()
  const filters = useAppSelector(selectActiveFilters)
  const facets = useAppSelector(selectFacets)

  const chips = activeFilterChips(filters, t, facets)

  if (chips.length === 0) return null

  return (
    <Flex
      wrap
      gap={8}
      align="center"
      role="group"
      aria-label={t("globalMap.filters.activeFiltersGroupLabel")}
      data-testid="global-map-filter-chips"
    >
      {chips.map(({ key, label, cleared }) => (
        <span
          key={key}
          data-testid="active-filter-chip"
          className="inline-flex h-8 items-center gap-2 rounded-full border border-blue-200 bg-blue-50 px-3 text-[13px] font-medium text-blue-700"
        >
          {label}
          <button
            type="button"
            aria-label={t("globalMap.filters.removeFilter", { filter: label })}
            onClick={() => dispatch(setFilters(cleared))}
            className="flex cursor-pointer items-center leading-none text-blue-500 hover:text-blue-700"
          >
            <CloseOutlined aria-hidden style={{ fontSize: 10 }} />
          </button>
        </span>
      ))}

      <button
        type="button"
        onClick={() => dispatch(clearFilters())}
        className="inline-flex h-8 cursor-pointer items-center gap-1.5 rounded-full border border-slate-300 bg-white px-3 text-[13px] font-medium text-slate-600 transition-colors hover:border-slate-400 hover:text-slate-900"
      >
        <CloseOutlined aria-hidden style={{ fontSize: 10 }} />
        {t("globalMap.filters.clearAll")}
      </button>
    </Flex>
  )
}

export default FilterChips
